import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import useMenuStore from 'src/store/useMenuStore';
import { FolderOutlined, FileOutlined, FolderOpenTwoTone, ReloadOutlined } from '@ant-design/icons';
import useUiuxStore from 'src/store/useUiuxStore';
import { MenuDto } from 'src/dtos/menu/menuDto';
import { getMenuComponent } from 'src/utils/menu/menuUtil';
import useUserStore from 'src/store/useUserStore';
import { getVersion } from 'src/apis/versionApi';
import { VersionDto } from 'src/dtos/version/VersionDto';
import { showWarning } from 'src/utils/toast/toastUtil';
import { postProgramLog } from 'src/apis/programLogApi';
import { resetAllStores } from 'src/utils/store/resetAllStores';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useDescLanguageStore } from 'src/store/useDescLanguageStore';
import { DropDownForm } from 'src/components/common/form/DropdownForm';
import { useDescText } from 'src/hooks/useDescText';
import { DESC_BTN } from 'src/utils/descLanguage/descLanguageUtil';

interface Props {
	data: MenuDto;
	handleLogoutClick: () => void;
	refreshMesInfo: (messageNotice: boolean) => void;
}

const Sidebar = ({ data, handleLogoutClick, refreshMesInfo }: Props) => {
	const { 
		tabList, 
		activeTab, setActiveTab,
		addTabList,
		sidebarVisible,
	} = useUiuxStore();
	const { userInfo } = useUserStore();
	const { menuList } = useMenuStore();
	const { languageList } = useCommonCodeStore();
	const { selectedLanguageCd, setSelectedLanguageCd } = useDescLanguageStore();
	const { renderDescText, getFontSize, getTextWrap } = useDescText();
	const menuFontSize = getFontSize("sidebarMenu");
	const allowMenuWrap = getTextWrap("sidebarMenu");

	const [programCodes, setProgramCodes] = useState<VersionDto>();

	//버전 확인후 버전이 다르면 리로드
	useEffect(() => {
		const fetchProgramCodes = async () => {
			try {
				const res = await getVersion();
				setProgramCodes(res.data.data);
				const fetchedVersion = res.data.data?.systemVersion;
				if (fetchedVersion) {
					const storedVersion = localStorage.getItem('systemVersion');
					if (storedVersion !== fetchedVersion) {
						localStorage.setItem('systemVersion', fetchedVersion);
						resetAllStores();
						(window.location as any).reload(true);
					}
				}else{
					showWarning('시스템 버전 정보를 확인할 수 없습니다.');
					localStorage.setItem('systemVersion', '0.0.0');
				}
			} catch (e) {
				setProgramCodes(undefined);
			}
		};
		fetchProgramCodes();
	}, []);

	useEffect(() => {
		if (languageList.length === 0) return;

		const isValidLanguage = languageList.some((lang) => lang.minorCd === selectedLanguageCd);
		if (isValidLanguage) return;

		const defaultLanguage = languageList.find((lang) => lang.minorCd === "KO") ?? languageList[0];
		setSelectedLanguageCd(defaultLanguage.minorCd);
	}, [languageList, selectedLanguageCd, setSelectedLanguageCd]);

	const handleLanguageChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedLanguageCd(e.target.value);
	}, [setSelectedLanguageCd]);
	
	const handleMenuClick = useCallback((menu: MenuDto) => {
		if (menu.menuCd) {
			void postProgramLog({
				programId: menu.menuCd,
				actionType: "OPEN",
				deviceType: "KIOSK",
			});
		}

		const exist = tabList.find(value => value.key === menu.menuCd);
		if (exist) {
			setActiveTab(exist);
		} else {
			const newTab = {
				key: menu.menuCd,
				title: renderDescText(menu.menuCd, menu.menuNm),
				component: getMenuComponent(menu.menuCd)
			};
			addTabList(newTab);
			setActiveTab(newTab);
		}
	}, [tabList, renderDescText, setActiveTab, addTabList]);

	return (
		<div 
			style={{ width: sidebarVisible ? '250px' : '0px' }}
			className='h-full flex shrink-0 flex-col bg-[#182744] transition-all duration-300 ease-in-out'
			>
			<div className="flex flex-col flex-1 overflow-hidden">

				{sidebarVisible && languageList.length > 0 && (
					<div className="w-full px-[8px] pt-[8px] shrink-0">
						<DropDownForm
							title="언어"
							data={languageList}
							value={selectedLanguageCd}
							onChange={handleLanguageChange}
							initLabelLess={true}
							titleless={true}
							wrapperOption={{
								wrapperStyle: "w-full mb-[4px]",
								titleStyle: "w-[60px] min-w-[60px] text-left text-white",
								inputStyle: "flex-1 min-w-0 [&>select]:h-[37px] [&>select]:rounded-[10px]",
							}}
						/>
					</div>
				)}

				{sidebarVisible && (
					<div className="flex h-[64px] shrink-0 items-center gap-[10px] px-[16px]">
						<img src="/images/logo/IMAGE.jpg" className="h-[34px] w-[34px] shrink-0 object-contain" alt="로고" />
						<span className="truncate text-[16px] font-bold text-white">
							Raonzena KIOSK
						</span>
					</div>
				)}
				
				{/* 메뉴 리스트 (내용이 많아지면 스크롤) */}
				<div className='hide-scrollbar flex-1 w-full flex flex-col px-[10px] overflow-y-auto'>
					{menuList.map((menu) => 
						<div 
							key={menu.menuCd}
							className={`mb-[2px] flex w-full shrink-0 cursor-pointer gap-x-4 rounded-[10px] px-[16px] font-semibold transition-colors ${
								activeTab?.key === menu.menuCd
									? "bg-[#2F64E9] text-white shadow-[0_6px_14px_rgba(47,100,233,0.22)]"
									: "text-[#DDE8FF] hover:bg-[#24395F] hover:text-white"
							} ${
								allowMenuWrap
									? "min-h-[50px] items-start py-[8px]"
									: "h-[50px] items-center truncate"
							}`}
							onClick={() => handleMenuClick(menu)}
						>
							{/* <FolderOutlined /> */}
							<span
								className={`${!sidebarVisible ? "hidden" : ""} ${
									allowMenuWrap
										? "w-full min-w-0 whitespace-normal break-words leading-tight"
										: "truncate"
								}`}
								style={{ fontSize: menuFontSize }}
							>
								{renderDescText(menu.menuCd, menu.menuNm)}
							</span>
						</div>
					)}
				</div>
				
				{/* 하단 영역 */}
				<div className={`${sidebarVisible ? "w-full shrink-0 border-t border-white/10 bg-[#15223D] p-[12px] text-white" : "hidden"}`}>
					<div className="flex w-full items-center gap-[10px]">
						<button
							onClick={() => refreshMesInfo(true)}
							className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[8px] bg-white/10 text-white transition-colors hover:bg-white/20"
							aria-label="정보 새로고침"
						>
							<ReloadOutlined />
						</button>
						<div className="ml-auto flex min-w-0 items-center gap-[10px]">
							<p className="min-w-0 truncate text-[13px] font-semibold text-[#DDE8FF]">{userInfo?.userNm}님</p>
							<button className="h-[34px] shrink-0 truncate rounded-[7px] bg-[#EF4D5A] px-[12px] text-[13px] font-bold text-white hover:bg-[#DC3E4B]" onClick={handleLogoutClick}>
								{renderDescText(DESC_BTN.LOGOUT, "로그아웃")}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Sidebar;
